import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function groupOrderItems(orderItm) {
  const grouped = {};
  const allSizesSet = new Set();

  orderItm.forEach((item) => {
    allSizesSet.add(item.SIZE_CD);
  });

  orderItm.forEach((item) => {
    const key = `${item.ITEM_CD}`;

    if (!grouped[key]) {
      const sizes = {};
      allSizesSet.forEach((size) => {
        sizes[size] = 0;
      });

      // Get Packing PCS from variant
      let packingPCS = 0;
      if (item.item && item.item.variant && Array.isArray(item.item.variant)) {
        const matched = item.item.variant.find(
          (v) => v.SIZE_CD === item.SIZE_CD,
        );
        packingPCS = matched ? parseInt(matched.DESC1) || 0 : 0;
      }

      grouped[key] = {
        ITEM_CD: item.ITEM_CD,
        ITEM_NAME: item.ITEM_NAME || item.item?.ITEM_NAME || "",
        RATE: item.RATE,
        item: item.item,
        sizes: sizes,
        totalQty: 0,
        totalPCS: 0,
        packingPCS: packingPCS,
        tax: 0,
        total: 0,
        REMARKS1: "",
        SIZE_CD: item.SIZE_CD,
        ISTATUS: item.ISTATUS || "",
      };
    }

    const size = item.SIZE_CD;
    const qty = item.QUANTITY || 0;
    const packingPCS = grouped[key].packingPCS;

    grouped[key].sizes[size] += qty;
    grouped[key].totalQty += qty;
    grouped[key].totalPCS += qty * packingPCS;
    grouped[key].tax += (item.NET_AMOUNT || 0) - (item.RATE || 0) * qty;
    grouped[key].total += item.NET_AMOUNT || 0;

    if (
      item.REMARKS1 &&
      item.REMARKS1.trim() !== "" &&
      !grouped[key].REMARKS1
    ) {
      grouped[key].REMARKS1 = item.REMARKS1;
    }
  });

  const groupedItems = Object.values(grouped);

  // Smart size sorting (same as backend)
  const labelOrder = [
    "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL",
    "4XL", "5XL", "6XL", "7XL", "8XL", "9XL",
  ];

  const isNumeric = (val) => /^\d+$/.test(val);

  const allSizes = Array.from(allSizesSet).sort((a, b) => {
    const aIndex = labelOrder.indexOf(a);
    const bIndex = labelOrder.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    if (isNumeric(a) && isNumeric(b)) return parseInt(a) - parseInt(b);
    if (isNumeric(a)) return -1;
    if (isNumeric(b)) return 1;
    return a.localeCompare(b);
  });

  const totalQtySum = groupedItems.reduce((sum, item) => sum + item.totalQty, 0);
  const totalPcsSum = groupedItems.reduce((sum, item) => sum + item.totalPCS, 0);

  return { groupedItems, allSizes, totalQtySum, totalPcsSum };
}

// ─── Format Date ─────────────────────────────────────────────
function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// ─── Escape HTML ─────────────────────────────────────────────
function esc(val) {
  if (val === null || val === undefined) return "";
  return String(val)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ─── CSS ─────────────────────────────────────────────────────
const PDF_CSS = `
  body {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
    color: #000;
    font-size: 12px;
    margin: 10px;
  }

  .header,
  .footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .company-details {
    text-align: right;
  }

  .company-details img {
    width: 150px;
    margin-bottom: 10px;
  }

  h1 {
    font-size: 14px;
    margin-bottom: 10px;
    text-align: center;
  }

  .meta-info {
    margin: 20px 0;
  }

  .meta-info p {
    margin: 3px 0;
  }

  .info-table {
    width: 100%;
    margin: 30px 0;
    border-collapse: collapse;
  }

  .info-table th,
  .info-table td {
    padding: 8px;
    border: none;
    vertical-align: top;
  }

  .info-table th {
    text-align: left;
    width: 50%;
  }

  .items-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
  }

  .items-table th,
  .items-table td {
    border: 0.5px solid #666;
    padding: 10px;
    text-align: left;
  }

  .items-table th {
    background-color: #f2f2f2;
  }

  .totals {
    float: right;
    width: 40%;
    margin-top: 30px;
  }

  .totals table {
    width: 100%;
    border-collapse: collapse;
  }

  .totals td {
    padding: 6px;
  }

  .totals tr td:last-child {
    text-align: right;
  }

  .comments {
    margin-top: 40px;
  }

  .comments strong {
    display: block;
    margin-bottom: 6px;
  }

  .footer-note {
    margin-top: 80px;
    font-size: 12px;
    color: #888;
    text-align: center;
  }

  .header {
    margin-bottom: 20px;
  }

  .order-meta {
    width: 100%;
    border-collapse: collapse;
  }

  .order-meta td {
    padding: 6px 8px;
    vertical-align: top;
  }

  .order-meta td strong {
    color: #333;
  }

  .order-meta tr:nth-child(odd) {
    background-color: #f2f2f2;
  }

  .order-meta tr:nth-child(even) {
    background-color: #ffffff;
  }

  .order-meta td {
    border: 0.5px solid #666;
  }

  .order-meta td:nth-child(odd) {
    width: 20%;
    font-weight: bold;
  }

  .order-meta td:nth-child(even) {
    width: 30%;
  }

  .company-info {
    text-align: center;
    margin-bottom: 20px;
  }
  .company-info h2 {
    margin: 0;
    font-size: 18px;
    text-transform: uppercase;
    padding-top: 2px;
  }
  .company-info p {
    margin: 0;
    font-size: 12px;
  }
  tr.total-row {
    page-break-inside: avoid;
  }

  /* Multi-order page break support */
  .page-break {
    page-break-after: always;
  }
  @media print {
    .page-break { page-break-after: always; }
  }
`;

// ─── Generate header-only HTML (no items table) ───────────────
function generateHeaderHtml(order, companyInfo) {
  const compName = esc(companyInfo?.COMP_NAME || "");
  const compAddr = esc(companyInfo?.ADDRS || "");
  const compPhone = esc(companyInfo?.PH1 || "");
  const addressParts = [
    order.accounts?.ADD1, order.accounts?.ADD2, order.accounts?.ADD3,
    order.accounts?.AREA, order.accounts?.CITY, order.accounts?.PINCODE,
  ].filter(Boolean);
  const fullAddress = esc(addressParts.join(" "));

  return `
    <h1>Sales Order</h1>
    <div class="company-info">
      <h2>${compName}</h2>
      <p>${compAddr}</p>
      <p>Phone: ${compPhone}</p>
    </div>
    <div class="header">
      <table class="order-meta">
        <tr>
          <td><strong>Order No:</strong></td>
          <td>#${esc(order.ORDER_NO)}</td>
          <td><strong>Date:</strong></td>
          <td>${formatDate(order.createdAt)}</td>
        </tr>
        <tr>
          <td><strong>Party Name:</strong></td>
          <td>${esc(order.accounts?.ACC_NAME)}</td>
          <td><strong>Party Mobile No:</strong></td>
          <td>${esc(order.accounts?.PHONE1)}</td>
        </tr>
        <tr>
          <td><strong>Party Address:</strong></td>
          <td colspan="3">${fullAddress}</td>
        </tr>
        <tr>
          <td><strong>Salesman Name:</strong></td>
          <td>${esc(order.sman?.SMAN_NAME)}</td>
          <td><strong>Distributor Name:</strong></td>
          <td>${esc(order.accounts?.distributor?.ACC_NAME)}</td>
        </tr>
        <tr>
          <td><strong>Customer Name:</strong></td>
          <td>${esc(order.REMARKS1)}</td>
          <td><strong>Customer Mobile No:</strong></td>
          <td>${esc(order.REMARKS2)}</td>
        </tr>
      </table>
    </div>
  `;
}

// ─── Generate Single Order HTML ──────────────────────────────
export function generateSingleOrderHtml(order, companyInfo) {
  const { groupedItems, allSizes, totalQtySum, totalPcsSum } = groupOrderItems(
    order.orderItm || [],
  );

  const sizeHeaders = allSizes.map((s) => `<th>${esc(s)}</th>`).join("");

  const itemRows = groupedItems
    .map((item, idx) => {
      const sizeCells = allSizes
        .map((s) => `<td>${item.sizes[s] || 0}</td>`)
        .join("");
      const deptName = item.item?.dept?.dept_name
        ? esc(item.item.dept.dept_name)
        : "-";
      return `<tr>
        <td>${idx + 1}</td>
        <td>${esc(item.ITEM_CD)}</td>
        <td>${deptName}</td>
        <td>${esc(item.ITEM_NAME)}</td>
        <td>${item.packingPCS || "-"}</td>
        ${sizeCells}
        <td><strong>${item.totalQty}</strong></td>
        <td><strong>${item.totalPCS}</strong></td>
        <td>${item.ISTATUS ? esc(item.ISTATUS.charAt(0)) : "-"}</td>
        <td>${item.REMARKS1 ? esc(item.REMARKS1) : "-"}</td>
      </tr>`;
    })
    .join("");

  const totalColspan = 5 + allSizes.length;

  return `
    ${generateHeaderHtml(order, companyInfo)}
    <table class="items-table">
      <thead>
        <tr>
          <th>No</th>
          <th>Lot No</th>
          <th>Dept</th>
          <th>Item</th>
          <th>Packing PCS</th>
          ${sizeHeaders}
          <th>Qty</th>
          <th>Total PCS</th>
          <th>Status</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
        <tr class="total-row">
          <td colspan="${totalColspan}"><strong>Total</strong></td>
          <td><strong>${totalQtySum}</strong></td>
          <td><strong>${totalPcsSum}</strong></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <div class="comments">
      <strong>Terms &amp; Condition</strong>
      <p>1. Payment terms 30 days.</p>
      <p>2. Goods once sold will not be taken back or Exchanged</p>
    </div>
  `;
}

// ─── Generate Multi Order HTML ───────────────────────────────
export function generateMultiOrderHtml(orders, companyInfo) {
  return orders
    .map((order, index) => {
      const html = generateSingleOrderHtml(order, companyInfo);
      if (index < orders.length - 1) {
        return `<div class="page-break">${html}</div>`;
      }
      return `<div>${html}</div>`;
    })
    .join("");
}


// ─── Export single order PDF using autotable for items ────────
export async function exportOrderPdf(order, companyInfo, filename) {
  const { groupedItems, allSizes, totalQtySum, totalPcsSum } = groupOrderItems(order.orderItm || []);

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const margin = 10;

  // 1. Company header via jsPDF text
  let y = margin;
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "bold");
  pdf.text("Sales Order", pdfWidth / 2, y + 4, { align: "center" });
  y += 10;

  pdf.setFontSize(13);
  pdf.text((companyInfo?.COMP_NAME || "").toUpperCase(), pdfWidth / 2, y + 4, { align: "center" });
  y += 7;

  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  if (companyInfo?.ADDRS) {
    pdf.text(companyInfo.ADDRS, pdfWidth / 2, y + 4, { align: "center" });
    y += 5;
  }
  if (companyInfo?.PH1) {
    pdf.text(`Phone: ${companyInfo.PH1}`, pdfWidth / 2, y + 4, { align: "center" });
    y += 5;
  }
  y += 3;

  // 2. Order meta table
  const addressParts = [
    order.accounts?.ADD1, order.accounts?.ADD2, order.accounts?.ADD3,
    order.accounts?.AREA, order.accounts?.CITY, order.accounts?.PINCODE,
  ].filter(Boolean);
  const fullAddress = addressParts.join(" ");

  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return `${String(date.getDate()).padStart(2,"0")}/${String(date.getMonth()+1).padStart(2,"0")}/${date.getFullYear()}`;
  };

  autoTable(pdf, {
    startY: y,
    body: [
      ["Order No:", `#${order.ORDER_NO || ""}`, "Date:", formatDate(order.createdAt)],
      ["Party Name:", order.accounts?.ACC_NAME || "", "Party Mobile No:", order.accounts?.PHONE1 || ""],
      ["Party Address:", { content: fullAddress, colSpan: 3 }],
      ["Salesman Name:", order.sman?.SMAN_NAME || "", "Distributor Name:", order.accounts?.distributor?.ACC_NAME || ""],
      ["Customer Name:", order.REMARKS1 || "", "Customer Mobile No:", order.REMARKS2 || ""],
    ],
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2, textColor: [0,0,0], lineColor: [102,102,102], lineWidth: 0.1 },
    bodyStyles: { fillColor: false },
    alternateRowStyles: { fillColor: [242, 242, 242] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 32 },
      1: { cellWidth: 58 },
      2: { fontStyle: "bold", cellWidth: 32 },
      3: { cellWidth: 58 },
    },
    margin: { left: margin, right: margin },
  });

  const headerHeightMm = pdf.lastAutoTable.finalY;

  // 2. Build autotable columns and rows
  const sizeColumns = allSizes.map((s) => ({ header: s, dataKey: s }));
  const columns = [
    { header: "No", dataKey: "no" },
    { header: "Lot No", dataKey: "lotNo" },
    { header: "Dept", dataKey: "dept" },
    { header: "Item", dataKey: "item" },
    { header: "Packing PCS", dataKey: "packingPCS" },
    ...sizeColumns,
    { header: "Qty", dataKey: "qty" },
    { header: "Total PCS", dataKey: "totalPCS" },
    { header: "Status", dataKey: "status" },
    { header: "Notes", dataKey: "notes" },
  ];

  const rows = groupedItems.map((item, idx) => {
    const row = {
      no: idx + 1,
      lotNo: item.ITEM_CD,
      dept: item.item?.dept?.dept_name || "-",
      item: item.ITEM_NAME,
      packingPCS: item.packingPCS || "-",
      qty: item.totalQty,
      totalPCS: item.totalPCS,
      status: item.ISTATUS ? item.ISTATUS.charAt(0) : "-",
      notes: item.REMARKS1 || "-",
    };
    allSizes.forEach((s) => { row[s] = item.sizes[s] || 0; });
    return row;
  });

  // Total row
  const totalRow = { no: "", lotNo: "", dept: "", item: "Total", packingPCS: "", qty: totalQtySum, totalPCS: totalPcsSum, status: "", notes: "" };
  allSizes.forEach((s) => { totalRow[s] = ""; });

  // 3. Add autotable — starts just below the header image
  autoTable(pdf, {
    startY: margin + headerHeightMm + 3,
    columns,
    body: [...rows, totalRow],
    theme: "grid",
    styles: {
      fontSize: 7,
      cellPadding: 2,
      textColor: [0, 0, 0],
      lineColor: [102, 102, 102],
      lineWidth: 0.1,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [242, 242, 242],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center",
    },
    bodyStyles: { halign: "center" },
    columnStyles: {
      item: { halign: "left", cellWidth: 30 },
      dept: { halign: "left" },
      notes: { halign: "left" },
    },
    didParseCell: (data) => {
      // Bold total row
      if (data.row.index === rows.length) {
        data.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: margin, right: margin },
  });

  // 4. Terms & conditions after table
  const finalY = pdf.lastAutoTable.finalY + 8;
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.text("Terms & Condition", margin, finalY);
  pdf.setFont("helvetica", "normal");
  pdf.text("1. Payment terms 30 days.", margin, finalY + 5);
  pdf.text("2. Goods once sold will not be taken back or Exchanged", margin, finalY + 10);

  pdf.save(`${filename || "Sales_Order"}.pdf`);
}

// ─── Export Orders PDF (legacy — used for multi-order export) ─
export function exportOrdersPdf(bodyHtml, filename) {
  const fullHtml = `<div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #000; font-size: 12px; width: 794px;">
    <style>${PDF_CSS}</style>
    ${bodyHtml}
  </div>`;

  const container = document.createElement("div");
  container.innerHTML = fullHtml;
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "0";
  document.body.appendChild(container);

  html2canvas(container, {
    scale: 2,
    useCORS: true,
    logging: false,
    width: 794,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/jpeg", 0.98);
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 10;

    pdf.addImage(imgData, "JPEG", 10, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight - 20;

    while (heightLeft > 0) {
      position = -(pdfHeight - 20) * Math.ceil((imgHeight - heightLeft) / (pdfHeight - 20)) + 10;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 20;
    }

    pdf.save(`${filename || "Sales_Order"}.pdf`);
    document.body.removeChild(container);
  }).catch(() => {
    document.body.removeChild(container);
  });
}
